// ReactionPicker.js - Emoji picker for message reactions
import React from 'react';
import { Popover, Paper, IconButton, Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

// Map reaction types to emojis
const REACTION_EMOJIS = {
  like: '👍',
  love: '❤️',
  laugh: '😂',
  wow: '😮',
  sad: '😢',
  angry: '😠'
};

const REACTION_LABELS = {
  like: 'Like',
  love: 'Love',
  laugh: 'Laugh',
  wow: 'Wow',
  sad: 'Sad',
  angry: 'Angry'
};

export default function ReactionPicker({
  open,
  anchorEl,
  onClose,
  onReactionSelect,
  currentReactions = []
}) {
  const theme = useTheme();
  const activeReactions = Array.isArray(currentReactions) ? currentReactions : [];

  const handleReactionClick = (reactionType) => {
    onReactionSelect(reactionType);
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      disableRestoreFocus
      disableAutoFocus
      disableEnforceFocus
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      PaperProps={{
        elevation: 8,
        sx: {
          borderRadius: 3,
          bgcolor: alpha(theme.palette.background.paper, 0.95),
          backdropFilter: 'blur(10px)',
          border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        }
      }}
    >
      <Paper 
        sx={{ 
          p: 1.5,
          minWidth: 280,
          bgcolor: 'transparent',
          boxShadow: 'none'
        }}
      >
        <Typography 
          variant="caption" 
          sx={{ 
            px: 1, 
            mb: 1, 
            display: 'block',
            color: 'text.secondary',
            fontWeight: 500
          }}
        >
          React to this message
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {Object.entries(REACTION_EMOJIS).map(([type, emoji]) => {
            const isSelected = activeReactions.includes(type);
            return (
              <IconButton
                key={type}
                onClick={() => handleReactionClick(type)}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  border: isSelected
                    ? `2px solid ${theme.palette.primary.main}` 
                    : `2px solid transparent`,
                  bgcolor: isSelected
                    ? alpha(theme.palette.primary.main, 0.1)
                    : 'transparent',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    transform: 'scale(1.1)',
                  },
                  transition: 'all 0.2s ease',
                  fontSize: '1.25rem',
                }}
                title={REACTION_LABELS[type]}
              >
                <span style={{ fontSize: '1.25rem' }}>{emoji}</span>
              </IconButton>
            );
          })}
        </Box>
        
        {activeReactions.length > 0 && (
          <Box sx={{ mt: 1.5, pt: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
              Click a selected reaction to remove it
            </Typography>
          </Box>
        )}
      </Paper>
    </Popover>
  );
}